import type { StoryblokSlide } from '@/sbTypes'
import { useSessionStore } from '@/stores/session'
import type { AnnotatedDeck, SlideAnalytics } from '@/types'
import { DateTime, Interval } from 'luxon'
import { onMounted, ref } from 'vue'

export const useSessionAnalytics = () => {
	const session = useSessionStore()

	const slideStartTime = ref<DateTime>(DateTime.now())

	onMounted(() => {
		const activeSession = session.activeSession
		if (!activeSession) {
			return
		}

		const isNewSlideDeck =
			activeSession.slideDeckId !== activeSession.analytics?.slideDeckId

		if (isNewSlideDeck) {
			setupNewAnalytics()
			session.saveSession(activeSession)
		}
	})

	const setupNewAnalytics = () => {
		const activeSession = session.activeSession
		if (!activeSession) {
			return
		}

		const presentationDeck = session.activeSlideDeck
		const slides = initialSlideAnalytics(presentationDeck)
		activeSession.analytics = {
			presentationStartAt: DateTime.now().toISO(),
			slideDeckId: activeSession.slideDeckId,
			slideDeckLocalization: activeSession.language as string,
			slideDeckVersion: presentationDeck?.published_at ?? undefined,
			slides,
		}
	}

	const initialSlideAnalytics = (
		presentation: AnnotatedDeck | undefined
	): SlideAnalytics[] => {
		if (!presentation?.content?.chapters) {
			return []
		}
		let netIndex = 0
		const slidesTest = presentation.content.chapters.flatMap(
			(chapter, chapterIndex) => {
				if (!chapter.slides || chapter.slides?.length === 0) {
					return
				}
				const chapterSlides = chapter.slides?.map(
					(slide: StoryblokSlide, slideIndex) => {
						return {
							id: slide._uid,
							title: slide.title,
							chapterIndex: chapterIndex, // 1 if the slide is in the second chapter of the deck
							indexInChapter: slideIndex, // 1 if the slide is the second slide of the chapter
							netIndex: netIndex + slideIndex, // index out of all the slides
							secondsOnSlide: 0, // total time from all visits
						}
					}
				)
				netIndex += chapter.slides?.length
				return chapterSlides
			}
		)

		return slidesTest.filter((slide) => slide !== undefined)
	}

	async function updateSlideAnalytics(index: number) {
		const duration = Math.round(
			Interval.fromDateTimes(slideStartTime.value, DateTime.now()).length(
				'seconds'
			)
		)

		const previousSlide = session.activeSession?.analytics?.slides.find(
			(slide) => slide?.netIndex === index
		)

		if (previousSlide) {
			previousSlide.secondsOnSlide += duration
		}

		await session.saveActiveSession()

		// reset slideStartTime after each change
		slideStartTime.value = DateTime.now()
	}

	return { updateSlideAnalytics }
}
